/** Date-only values ("YYYY-MM-DD") must never go through `new Date(value)`.
 *
 *  ECMA-262 parses a bare date string as UTC midnight, so `toLocaleDateString`
 *  in any timezone west of Greenwich renders the *previous* day: a DOB stored
 *  as 1948-03-22 displayed as "Mar 21, 1948". Timestamps carry an explicit time
 *  (and usually an offset), are unambiguous, and pass through untouched.
 *
 *  MySQL hands date columns back as either "1948-03-22" or "1948-03-22 00:00:00",
 *  so both spellings count as date-only here.
 */

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})(?:[T ]00:00:00(?:\.000)?Z?)?$/;

/** Parse to a Date anchored at LOCAL midnight when the value is date-only,
 *  otherwise defer to the normal Date parser. Returns null when unparseable. */
export function parseDateOnly(value) {
  if (!value) return null;
  const raw = String(value).trim();

  const m = DATE_ONLY.exec(raw);
  if (m) {
    const [, year, month, day] = m;
    const d = new Date(Number(year), Number(month) - 1, Number(day));
    return isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

/** "Mar 22, 1948". `fallback` is returned for empty/unparseable input. */
export function formatDate(value, fallback = "—") {
  const d = parseDateOnly(value);
  if (!d) return fallback;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** Whole years elapsed, comparing local calendar dates on both sides so a
 *  birthday never lands a day early. Returns null when unparseable. */
export function ageInYears(value, now = new Date()) {
  const born = parseDateOnly(value);
  if (!born) return null;

  let age = now.getFullYear() - born.getFullYear();
  const monthDiff = now.getMonth() - born.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < born.getDate())) age -= 1;
  return age;
}

/** The LOCAL calendar day of a Date as "YYYY-MM-DD", or "" when unusable.
 *  `toISOString().slice(0, 10)` is the UTC day instead: from 20:00 EDT it is
 *  already tomorrow, which made HoldTimeSelector treat a hold starting today
 *  as a scheduled one and show its end date a day late. */
export function toLocalYMD(value) {
  if (!(value instanceof Date) || isNaN(value.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
}
